import { useQuery } from "@tanstack/react-query"
import { useState, type FormEvent } from "react"
import { api, toApiMessage } from "../../api/client"
import { Button, ErrorState, Field, FormSection, StickyActionBar } from "../../components/ui"
import { AudienceField, audienceMemberIds, useAudienceOptions, type Audience } from "../family/AudienceField"
import type { TaskDefinition } from "./types"

type CurrencyOption = { id: string; name: string; icon: string }
type AbilityOption = { id: string; name: string; icon: string }
type AchievementOption = { id: string; name: string; icon: string }

export type TaskFormValue = {
  title: string
  description: string
  assignmentStrategy: "ANY" | "ALL" | "ROTATION"
  assigneeAudience: Audience
  availableFrom?: string | undefined
  dueAt?: string | null
  recurrence?: { frequency: string; interval: number } | null
  completionMode: "TRUST" | "REQUIRE_APPROVAL"
  approverAudience?: Audience | null
  approverMode: "ANY_ONE" | "ALL"
  checklist: string[]
  reminders: number[]
  overduePolicy: string
  visibility: "FAMILY" | "PARTICIPANTS"
  skipAdvancesRotation: boolean
  rewards: Array<Record<string, unknown>>
  penalty?: { currencyId: string; amount: string } | null
}

export type TaskFormInitial = Partial<TaskDefinition> & {
  dueAt?: string | null | undefined
  availableFrom?: string | null | undefined
}

function isoToLocalDateTime(value?: string | null) {
  if (!value) return ""
  const date = new Date(value)
  return new Date(date.valueOf() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

function localDateTimeToIso(value: FormDataEntryValue | null) {
  const text = typeof value === "string" ? value.trim() : ""
  return text ? new Date(text).toISOString() : undefined
}

export function TaskForm({
  mode,
  initial,
  currentMemberId,
  assigneesLocked = false,
  recurrenceRequired = false,
  onSubmit,
  onCancel
}: {
  mode: "create" | "edit"
  initial?: TaskFormInitial
  currentMemberId: string
  assigneesLocked?: boolean
  recurrenceRequired?: boolean
  onSubmit(value: TaskFormValue): Promise<void>
  onCancel(): void
}) {
  const { familyId, groups } = useAudienceOptions()
  const [advanced, setAdvanced] = useState(mode === "edit")
  const [assignees, setAssignees] = useState<Audience>(initial?.assigneeAudience ?? { memberIds: [], groupIds: [] })
  const [approvers, setApprovers] = useState<Audience>(
    initial?.approverAudience ?? { memberIds: [currentMemberId], groupIds: [] }
  )
  const [strategy, setStrategy] = useState<"ANY" | "ALL" | "ROTATION">(initial?.assignmentStrategy ?? "ALL")
  const [recurrence, setRecurrence] = useState(initial?.recurrence?.frequency ?? (recurrenceRequired ? "DAILY" : "NONE"))
  const [approvalRequired, setApprovalRequired] = useState(initial?.completionMode === "REQUIRE_APPROVAL")
  const currencyReward = initial?.rewards?.find((reward) => reward.currency)
  const abilityReward = initial?.rewards?.find((reward) => reward.ability)
  const achievementReward = initial?.rewards?.find((reward) => reward.achievement)
  const [rewardCurrencyId, setRewardCurrencyId] = useState(currencyReward?.currency?.id ?? "")
  const [rewardAbilityId, setRewardAbilityId] = useState(abilityReward?.ability?.id ?? "")
  const [rewardAchievementId, setRewardAchievementId] = useState(achievementReward?.achievement?.id ?? "")
  const [penaltyCurrencyId, setPenaltyCurrencyId] = useState(initial?.penaltyCurrencyId ?? "")
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState("")
  const assigneeCount = audienceMemberIds(assignees, groups).length
  const canClaimOne = assignees.groupIds.length > 0 && assigneeCount > 1
  const effectiveStrategy = assigneeCount <= 1 || (!canClaimOne && strategy === "ANY") ? "ALL" : strategy
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: CurrencyOption[] }>(`/families/${familyId}/currencies`),
    enabled: Boolean(familyId)
  })
  const abilities = useQuery({
    queryKey: ["v1", familyId, "abilities"],
    queryFn: () => api.get<{ items: AbilityOption[] }>(`/families/${familyId}/abilities`),
    enabled: Boolean(familyId)
  })
  const achievements = useQuery({
    queryKey: ["v1", familyId, "achievements"],
    queryFn: () => api.get<{ items: AchievementOption[] }>(`/families/${familyId}/achievements`),
    enabled: Boolean(familyId)
  })

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setServerError("")
    const data = new FormData(event.currentTarget)
    const rewards: Array<Record<string, unknown>> = []
    if (rewardCurrencyId && data.get("rewardCurrencyAmount")) {
      rewards.push({ currencyId: rewardCurrencyId, currencyAmount: data.get("rewardCurrencyAmount") })
    }
    if (rewardAbilityId) {
      rewards.push({ abilityDefinitionId: rewardAbilityId, abilityCount: Number(data.get("rewardAbilityCount") || 1) })
    }
    if (rewardAchievementId) rewards.push({ achievementDefinitionId: rewardAchievementId })
    setSaving(true)
    try {
      await onSubmit({
        title: String(data.get("title") ?? "").trim(),
        description: String(data.get("description") ?? "").trim(),
        assignmentStrategy: effectiveStrategy,
        assigneeAudience: assignees,
        availableFrom: localDateTimeToIso(data.get("availableFrom")),
        dueAt: localDateTimeToIso(data.get("dueAt")) ?? null,
        recurrence:
          recurrence === "NONE"
            ? null
            : { frequency: recurrence, interval: Number(data.get("recurrenceInterval") || 1) },
        completionMode: approvalRequired ? "REQUIRE_APPROVAL" : "TRUST",
        approverAudience: approvalRequired ? approvers : null,
        approverMode: (data.get("approverMode") || "ANY_ONE") as "ANY_ONE" | "ALL",
        checklist: String(data.get("checklist") || "").split("\n").map((value) => value.trim()).filter(Boolean),
        reminders: data.get("reminderMinutes") ? [Number(data.get("reminderMinutes"))] : [],
        overduePolicy: String(data.get("overduePolicy") || "KEEP_OVERDUE"),
        visibility: (data.get("visibility") || "FAMILY") as "FAMILY" | "PARTICIPANTS",
        skipAdvancesRotation: data.get("skipAdvancesRotation") === "on",
        rewards,
        penalty:
          penaltyCurrencyId && data.get("penaltyAmount")
            ? { currencyId: penaltyCurrencyId, amount: String(data.get("penaltyAmount")) }
            : null
      })
    } catch (error) {
      setServerError(toApiMessage(error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="form-stack task-editor-form" onSubmit={submit}>
      {serverError ? <ErrorState title="Не удалось сохранить" message={serverError} /> : null}
      <FormSection title="Что сделать">
        <Field label="Что сделать">
          <input name="title" required maxLength={120} autoFocus defaultValue={initial?.title ?? ""} placeholder="Например, убрать кухню" />
        </Field>
        <Field label="Описание" hint="Коротко уточните ожидаемый результат">
          <textarea name="description" rows={3} defaultValue={initial?.description ?? ""} placeholder="Необязательно" />
        </Field>
        <Field label="Чек-лист" hint="Один пункт на строку">
          <textarea name="checklist" rows={4} defaultValue={initial?.checklist?.map((item) => item.title).join("\n") ?? ""} />
        </Field>
      </FormSection>

      <FormSection title="Кому">
        {assigneesLocked ? (
          <p className="field__hint">Исполнителей нельзя менять после отправки результата.</p>
        ) : (
          <AudienceField label="Кому назначена" value={assignees} onChange={setAssignees} />
        )}
        {!assigneesLocked && assigneeCount > 1 ? (
          <Field label="Как распределить">
            <select value={effectiveStrategy} onChange={(event) => setStrategy(event.target.value as typeof strategy)}>
              {canClaimOne ? <option value="ANY">Кто-нибудь один возьмёт задачу</option> : null}
              <option value="ALL">Каждый участник</option>
              <option value="ROTATION">По очереди</option>
            </select>
          </Field>
        ) : null}
      </FormSection>

      <FormSection title="Когда" optional={!recurrenceRequired}>
        <div className="form-grid">
          {mode === "create" ? (
            <Field label="Доступна с">
              <input name="availableFrom" type="datetime-local" defaultValue={isoToLocalDateTime(initial?.availableFrom)} />
            </Field>
          ) : null}
          <Field label="Сделать до">
            <input name="dueAt" type="datetime-local" defaultValue={isoToLocalDateTime(initial?.dueAt)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="Повтор">
            <select value={recurrence} onChange={(event) => setRecurrence(event.target.value)}>
              {!recurrenceRequired ? <option value="NONE">Не повторять</option> : null}
              <option value="DAILY">Каждый день</option>
              <option value="WEEKLY">Каждую неделю</option>
              <option value="MONTHLY">Каждый месяц</option>
            </select>
          </Field>
          {recurrence !== "NONE" ? (
            <Field label="Интервал" hint="Например, 2 — раз в два периода">
              <input name="recurrenceInterval" type="number" min="1" max="365" defaultValue={initial?.recurrence?.interval ?? 1} />
            </Field>
          ) : null}
        </div>
      </FormSection>

      <button type="button" className="advanced-toggle" aria-expanded={advanced} onClick={() => setAdvanced((value) => !value)}>
        {advanced ? "Скрыть дополнительные настройки" : "Дополнительные настройки"}
      </button>
      {advanced ? (
        <div className="advanced-box form-stack">
          <FormSection title="Приёмка и напоминание">
            <label className="check-row">
              <input type="checkbox" checked={approvalRequired} onChange={(event) => setApprovalRequired(event.target.checked)} />
              <span>Нужно подтверждение выполнения</span>
            </label>
            {approvalRequired ? (
              <>
                <AudienceField label="Кто подтверждает" value={approvers} onChange={setApprovers} />
                <Field label="Сколько подтверждений нужно">
                  <select name="approverMode" defaultValue={initial?.approverMode ?? "ANY_ONE"}>
                    <option value="ANY_ONE">Одного достаточно</option>
                    <option value="ALL">Нужны все</option>
                  </select>
                </Field>
              </>
            ) : null}
            <Field label="Напомнить заранее">
              <select name="reminderMinutes" defaultValue={initial?.reminders?.[0] ?? 60}>
                <option value="">Не напоминать</option>
                <option value="15">За 15 минут</option>
                <option value="60">За час</option>
                <option value="1440">За день</option>
              </select>
            </Field>
          </FormSection>

          <FormSection title="Награда" optional>
            <div className="form-grid">
              <Field label="Валюта">
                <select value={rewardCurrencyId} onChange={(event) => setRewardCurrencyId(event.target.value)}>
                  <option value="">Без валюты</option>
                  {(currencies.data?.items ?? []).map((item) => <option key={item.id} value={item.id}>{item.icon} {item.name}</option>)}
                </select>
              </Field>
              {rewardCurrencyId ? (
                <Field label="Количество">
                  <input name="rewardCurrencyAmount" inputMode="numeric" required defaultValue={currencyReward?.currencyAmount ?? ""} />
                </Field>
              ) : null}
            </div>
            <div className="form-grid">
              <Field label="Способность">
                <select value={rewardAbilityId} onChange={(event) => setRewardAbilityId(event.target.value)}>
                  <option value="">Без способности</option>
                  {(abilities.data?.items ?? []).map((item) => <option key={item.id} value={item.id}>{item.icon} {item.name}</option>)}
                </select>
              </Field>
              {rewardAbilityId ? (
                <Field label="Количество">
                  <input name="rewardAbilityCount" type="number" min="1" max="100" required defaultValue={abilityReward?.abilityCount ?? 1} />
                </Field>
              ) : null}
            </div>
            <Field label="Достижение">
              <select value={rewardAchievementId} onChange={(event) => setRewardAchievementId(event.target.value)}>
                <option value="">Без достижения</option>
                {(achievements.data?.items ?? []).map((item) => <option key={item.id} value={item.id}>{item.icon} {item.name}</option>)}
              </select>
            </Field>
          </FormSection>

          <FormSection title="Правила" optional>
            <div className="form-grid">
              <Field label="Штраф за пропуск">
                <select value={penaltyCurrencyId} onChange={(event) => setPenaltyCurrencyId(event.target.value)}>
                  <option value="">Без штрафа</option>
                  {(currencies.data?.items ?? []).map((item) => <option key={item.id} value={item.id}>{item.icon} {item.name}</option>)}
                </select>
              </Field>
              {penaltyCurrencyId ? (
                <Field label="Размер штрафа">
                  <input name="penaltyAmount" inputMode="numeric" required defaultValue={initial?.penaltyAmount ?? ""} />
                </Field>
              ) : null}
            </div>
            <Field label="Если срок прошёл">
              <select name="overduePolicy" defaultValue={initial?.overduePolicy ?? "KEEP_OVERDUE"}>
                <option value="KEEP_OVERDUE">Оставить просроченной</option>
                <option value="MARK_MISSED_AND_CONTINUE">Отметить пропущенной и продолжить</option>
                <option value="BLOCK_NEXT_UNTIL_CLOSED">Не создавать следующую</option>
              </select>
            </Field>
            <Field label="Кто видит задачу">
              <select name="visibility" defaultValue={initial?.visibility ?? "FAMILY"}>
                <option value="FAMILY">Вся семья</option>
                <option value="PARTICIPANTS">Только участники</option>
              </select>
            </Field>
            <label className="check-row">
              <input name="skipAdvancesRotation" type="checkbox" defaultChecked={initial?.skipAdvancesRotation ?? true} />
              <span>При пропуске передавать очередь следующему</span>
            </label>
          </FormSection>
        </div>
      ) : null}

      <StickyActionBar>
        <Button type="button" variant="ghost" onClick={onCancel}>Отмена</Button>
        <Button type="submit" loading={saving}>{mode === "create" ? "Создать задачу" : "Сохранить"}</Button>
      </StickyActionBar>
    </form>
  )
}
